/* Photo pipeline: decode -> downscale -> re-encode JPEG -> verify -> store.
 *
 * Three things this has to get right, in order of how badly the field feels them:
 *
 *  1. SIZE. Technicians are on bad connections, and a photo has to survive in an
 *     offline queue possibly for hours. A 12 MP phone JPEG is 4-6 MB; twenty of
 *     them will not drain over a warehouse's guest wifi. So: cap the long edge,
 *     then walk a quality ladder until the result fits a byte budget.
 *  2. EXIF, INCLUDING GPS. Re-encoding from pixels cannot carry metadata
 *     forward, which is the mechanism; `assertNoMetadata` is the check on it.
 *     The server ALSO re-encodes every upload (control C-36) and that remains
 *     the authoritative strip — this is defence in depth, not the only line.
 *  3. HONEST NUMBERS. The compressed size is returned and shown, because the
 *     technician is the only person who can tell that a photo is huge before it
 *     goes into the queue.
 *
 * The seam: everything that needs a browser (decode a Blob to pixels, draw it
 * scaled, encode a JPEG) sits behind `RasterPort`. The sizing maths, the quality
 * ladder, the retry policy and the verification are ordinary functions and are
 * unit-tested. The default port uses the DOM; the tests inject a Node one.
 */

import { assertNoMetadata, stripMetadataSegments } from './jpegMetadata';
import type { PhotoAttachment } from './types';

/** An opaque decoded image, whatever the port's drawable happens to be. */
export interface Raster {
  width: number;
  height: number;
  /** Port-private handle (ImageBitmap in the browser, Image in Node). */
  readonly source: unknown;
}

export interface RasterPort {
  /** Decode image bytes to a drawable. Rejects if the bytes are not an image. */
  decode(blob: Blob): Promise<Raster>;
  /** Draw `raster` scaled into w x h and encode as JPEG at `quality` (0..1). */
  reencode(raster: Raster, width: number, height: number, quality: number): Promise<Blob>;
  /** Free the drawable, where the platform has such a concept. */
  release?(raster: Raster): void;
}

export interface CompressOptions {
  /** Longest edge of the output, in pixels. */
  maxEdge: number;
  /** Stop the quality ladder once the output is at or under this. */
  targetBytes: number;
  /** Qualities to try, in order. */
  qualityLadder: readonly number[];
  /**
   * Hard ceiling. If even the last rung is over this, the photo is rejected
   * rather than silently queued — an unqueueable photo is worse than an error,
   * because the technician walks away believing the job is done.
   */
  hardMaxBytes: number;
}

/**
 * Defaults tuned for the job at hand: a serial plate or a bracket, legible at
 * 1600px on the long edge, and ~400 KB so twenty of them are ~8 MB of queue.
 */
export const DEFAULT_COMPRESS: CompressOptions = {
  maxEdge: 1600,
  targetBytes: 400 * 1024,
  qualityLadder: [0.72, 0.6, 0.5, 0.42, 0.34],
  hardMaxBytes: 4 * 1024 * 1024,
};

/** Scale w x h so the long edge is at most maxEdge. Never upscales. */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest === 0) {
    return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
  }
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export interface CompressedPhoto {
  blob: Blob;
  bytes: number;
  originalBytes: number;
  width: number;
  height: number;
  /** Which rung of the ladder produced this, for diagnostics. */
  quality: number;
  /** How many encode passes it took. */
  attempts: number;
}

export class PhotoTooLargeError extends Error {
  constructor(readonly bytes: number, readonly limit: number) {
    super(
      `photo is ${formatBytes(bytes)} after compression, over the ${formatBytes(limit)} limit`,
    );
    this.name = 'PhotoTooLargeError';
  }
}

export class PhotoDecodeError extends Error {
  constructor(cause?: unknown) {
    super('that file could not be read as an image');
    this.name = 'PhotoDecodeError';
    this.cause = cause;
  }
}

/**
 * Compress and strip. Pure apart from the injected port.
 *
 * The ladder walks quality first because dropping resolution costs legibility
 * on a stamped serial plate, and legibility is the entire point of the photo.
 */
export async function compressPhoto(
  input: Blob,
  port: RasterPort,
  options: CompressOptions = DEFAULT_COMPRESS,
): Promise<CompressedPhoto> {
  const originalBytes = input.size;

  let raster: Raster;
  try {
    raster = await port.decode(input);
  } catch (err) {
    throw new PhotoDecodeError(err);
  }

  try {
    const target = fitWithin(raster.width, raster.height, options.maxEdge);
    let best: { blob: Blob; quality: number } | null = null;
    let attempts = 0;

    for (const quality of options.qualityLadder) {
      attempts += 1;
      const blob = await port.reencode(raster, target.width, target.height, quality);
      if (!best || blob.size < best.blob.size) best = { blob, quality };
      if (blob.size <= options.targetBytes) break;
    }

    if (!best) throw new PhotoDecodeError(new Error('empty quality ladder'));

    /**
     * STRIP, THEN CHECK — the same shape as the server's sanitizer (C-36), and for the same
     * reason. Real browser encoders write metadata of their own into `canvas.toBlob` output:
     * Chrome mints an APP2 ICC profile, and WebKit (desktop AND mobile Safari) mints an APP1
     * `Exif` plus an APP13 `8BIM` segment on every encode — measured, see
     * `stripMetadataSegments`. Asserting directly on encoder output therefore rejected every
     * Safari photo. The pixels-only re-encode remains the mechanism that drops the CAMERA's
     * metadata (GPS included — the decoded raster never had it); the strip removes whatever
     * the ENCODER minted; and `assertNoMetadata` stays as the check on the stripper's own
     * output, so a stripper bug is a refused photo, never a stored leak.
     */
    const bytes = stripMetadataSegments(new Uint8Array(await best.blob.arrayBuffer()));
    assertNoMetadata(bytes);
    const cleanBlob = new Blob([bytes as BlobPart], { type: 'image/jpeg' });

    if (cleanBlob.size > options.hardMaxBytes) {
      throw new PhotoTooLargeError(cleanBlob.size, options.hardMaxBytes);
    }

    return {
      blob: cleanBlob,
      bytes: cleanBlob.size,
      originalBytes,
      width: target.width,
      height: target.height,
      quality: best.quality,
      attempts,
    };
  } finally {
    port.release?.(raster);
  }
}

/** Blob store contract owned by the core module. */
export interface BlobSink {
  putBlob(blob: Blob): Promise<string>;
}

/**
 * Full path: compress, strip, verify, hand the bytes to the blob store, return
 * the small record that goes in the outbox item. The bytes never go in the item.
 */
export async function capturePhoto(
  input: Blob,
  opts: {
    port: RasterPort;
    sink: BlobSink;
    source: 'camera' | 'library';
    label?: string;
    now?: () => number;
    compress?: CompressOptions;
  },
): Promise<PhotoAttachment> {
  const compressed = await compressPhoto(input, opts.port, opts.compress ?? DEFAULT_COMPRESS);
  const blobId = await opts.sink.putBlob(compressed.blob);
  return {
    blobId,
    mime: 'image/jpeg',
    bytes: compressed.bytes,
    originalBytes: compressed.originalBytes,
    width: compressed.width,
    height: compressed.height,
    at: (opts.now ?? Date.now)(),
    source: opts.source,
    ...(opts.label === undefined ? {} : { label: opts.label }),
  };
}

/** Sizes for humans. Deliberately coarse — the technician needs an order of magnitude. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** "1.2 MB -> 310 KB (74% smaller)", for the caption under a thumbnail. */
export function describeCompression(a: Pick<PhotoAttachment, 'bytes' | 'originalBytes'>): string {
  if (a.originalBytes <= 0) return formatBytes(a.bytes);
  const saved = Math.round((1 - a.bytes / a.originalBytes) * 100);
  if (saved <= 0) return `${formatBytes(a.originalBytes)} → ${formatBytes(a.bytes)}`;
  return `${formatBytes(a.originalBytes)} → ${formatBytes(a.bytes)} (${saved}% smaller)`;
}

/* ---------------------------------------------------------------- browser port */

/**
 * DOM implementation. `createImageBitmap` is used where available because it
 * decodes off the main thread; the `<img>` path is the iOS Safari fallback.
 *
 * NOTE: this function is not covered by the unit tests — it needs a real
 * browser. The logic above it is.
 */
export function domRasterPort(): RasterPort {
  return {
    async decode(blob) {
      if (typeof createImageBitmap === 'function') {
        const bitmap = await createImageBitmap(blob);
        return { width: bitmap.width, height: bitmap.height, source: bitmap };
      }
      const url = URL.createObjectURL(blob);
      try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = () => reject(new Error('image decode failed'));
          el.src = url;
        });
        return { width: img.naturalWidth, height: img.naturalHeight, source: img };
      } finally {
        URL.revokeObjectURL(url);
      }
    },

    async reencode(raster, width, height, quality) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      // White backdrop: a transparent PNG would otherwise flatten to black.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(raster.source as CanvasImageSource, 0, 0, width, height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality),
      );
      if (!blob) throw new Error('canvas encode failed');
      return blob;
    },

    release(raster) {
      const src = raster.source as { close?: () => void };
      src?.close?.();
    },
  };
}
